import { Button, Typography } from "@material-ui/core"
import * as api from "api"
import { Desktop, Mobile } from "common/MediaQueries"
import { trackPhoneShowroomPageClicks } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { memo, useRef } from "react"
import styled from "styled-components"
import { StyledRow, StyledSvgIcon } from "theme/shared"
import CardRowText from "../CardRowText"
import ManagerBox from "./ManagerBox"

const StyledWrapper = styled.div<{ $selected: boolean }>`
  margin: 0px 0px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${(p) => p.theme.colors.white};
  padding: 20px 15px;
  border-radius: 6px;
  border: 1.5px solid
    ${(p) =>
      p.$selected ? p.theme.colors.saphire : p.theme.colors.transparent};
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`
const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 50%;
  }
`

const StyledHeadline = styled(Typography).attrs({ variant: "h3" })`
  margin-top: 0;
  display: inline-block;
`

const StyledContentStart = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-items: start;
`
const StyledDivider = styled.div`
  display: flex;
  height: auto;
  flex-grow: 1;
  flex-shrink: 0;
  background-color: ${(p) => p.theme.colors.darkGrey};
  width: 0.5px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    display: none;
  }
`

const StyledIcon = styled(StyledSvgIcon)`
  margin-bottom: -4px;
  margin-top: -1px;
`

const StyledButtonWrap = styled.div`
  flex-grow: 1;
`

const StyledMobileIcon = styled(StyledSvgIcon)<{
  $left?: number
  $top?: number
  $flip?: boolean
}>`
  position: relative;
  ${(p) => p?.$left && `left: ${p.$left}px;`}
  ${(p) => p?.$top && `top: ${p.$top}px;`}
  ${(p) => p?.$flip && `transform: rotateY(180deg);`}
`

export enum Font {
  OENaPro = "OENaPro",
  MazdaType = "MazdaType, OENaPro, sans serif",
}

export enum FontSize {
  small = 16,
  large = 18,
}

type ShowRoomItemItemProps = {
  showRoom: api.ShowRoom
  isShowRoomSelected: boolean
}

const ShowRoomItem = ({
  showRoom,
  isShowRoomSelected,
}: ShowRoomItemItemProps) => {
  const ref = useRef(null)

  if (isShowRoomSelected) {
    ref.current?.scrollIntoView({
      block: "nearest",
    })
  }

  return (
    <StyledWrapper $selected={isShowRoomSelected} ref={ref}>
      <StyledColumn>
        <Desktop>
          <StyledIcon src="/icons/mazdaLocationIcon.svg" w={32} h={40} />
        </Desktop>
        <StyledHeadline>{showRoom?.name}</StyledHeadline>
        <CardRowText
          data={showRoom.address}
          iconName="mazdaLocationIcon"
          iconInMobile
          font={Font.OENaPro}
          fontSize={FontSize.large}
        />
        <CardRowText
          data={showRoom.whatsapp}
          link={
            "https://api.whatsapp.com/send/?phone=972532397240&text&type=phone_number&app_absent=0"
          }
          iconName="whatsappLogo"
          font={Font.MazdaType}
          fontSize={FontSize.small}
        />
        <CardRowText
          data={showRoom.phone}
          link={`tel:${showRoom.phone}`}
          iconName="phoneCall"
          font={Font.MazdaType}
          fontSize={FontSize.small}
          trackEvent={trackPhoneShowroomPageClicks}
        />
        <Desktop>
          {showRoom.fax && (
            <CardRowText
              data={showRoom.fax}
              iconName="fax"
              font={Font.MazdaType}
              fontSize={FontSize.small}
            />
          )}
        </Desktop>
      </StyledColumn>
      <StyledDivider />

      <StyledColumn>
        <Desktop>
          <StyledSvgIcon src="/icons/clock.svg" w={35} h={35} />
        </Desktop>
        <StyledContentStart>
          <Mobile>
            <StyledSvgIcon src="/icons/clock.svg" />
          </Mobile>
          <div>
            <StyledHeadline>שעות פעילות</StyledHeadline>
            {showRoom.stringShowRoomsSchedule.map((openingHour, index) => (
              <StyledRow key={index} $mt="7px">
                <CardRowText
                  data={openingHour}
                  font={Font.OENaPro}
                  fontSize={FontSize.large}
                />
              </StyledRow>
            ))}
          </div>
        </StyledContentStart>
        <ManagerBox showRoom={showRoom} />
      </StyledColumn>
      <Mobile>
        <StyledRow $gap="10px" $fullWidth>
          {showRoom.wazeLink && (
            <StyledButtonWrap>
              <NextLink href={showRoom.wazeLink} openInNewTab passHref>
                <Button variant="outlined" fullWidth>
                  <StyledMobileIcon
                    src="/icons/navigationArrow.svg"
                    w={15}
                    h={15}
                    $left={5}
                    $top={1}
                    $flip={true}
                  />
                  ניווט ב-Waze
                </Button>
              </NextLink>
            </StyledButtonWrap>
          )}
          {showRoom.phone && (
            <StyledButtonWrap>
              <NextLink href={`tel:${showRoom.phone}`} passHref>
                <Button variant="outlined" fullWidth>
                  <StyledMobileIcon
                    src="/icons/phoneCall.svg"
                    w={18}
                    h={18}
                    $left={5}
                  />
                  חיוג
                </Button>
              </NextLink>
            </StyledButtonWrap>
          )}
        </StyledRow>
      </Mobile>
    </StyledWrapper>
  )
}

export default memo(ShowRoomItem)
